import { ChevronLeftIcon, ChevronRightIcon } from '@heroicons/react/24/outline';
import classNames from 'classnames';
import { keyBy, omit } from 'lodash/fp';
import { memo, useCallback, useMemo, useContext } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import CalculatorIcon from '../../../assets/icons/calculator.svg?react';
import ChartBoxIcon from '../../../assets/icons/chart-box.svg?react';
import CommentsIcon from '../../../assets/icons/comments.svg?react';
import { Badge } from '../../../components/Badge';
import { TabPane, TabsContainer } from '../../../components/Tabs';
import { AnnotationContextGroup, ProjectionCalculation, VolumeCalculation } from '../../../contexts/AnnotationContext';
import { useCADObjects } from '../../../hooks/potree/useRenderer';
import { useAnnotationTree, useAnnotationLookup, useCalculationsByIdentifier } from '../../../hooks/potree/useAnnotationContext';
import useQueryParams from '../../../hooks/useQueryParams';
import { useCustomSelector } from '../../../redux/store';
import { useT } from '../../../translation/src';
import { ProjectShellFragment } from '../../../types/graphqlTypes';
import { replaceURLParams } from '../../../utils/URLSearchParams';
import { AreaCalculations } from './AreaCalculations';
import { AreaProperties, CadAreaProperties } from './AreaProperties';
import { OpeningProperties } from './OpeningProperties';
import { CalculationList } from './CalculationList';
import { Comments } from './Comments';
import { isShareMode } from '../../../share/shareMode';
import { RendererReadOnlyContext } from '../../../contexts/RendererReadOnlyContext';
import { DistanceCalculations } from './DistanceCalculations';
import { CadDistanceProperties, DistanceProperties } from './DistanceProperties';
import { GroupCalculations } from './GroupCalculations';
import { GroupProperties } from './GroupProperties';
import { FloorProperties } from './FloorProperties';
import { SurfaceProperties } from './SurfaceProperties';
import { MapProperties } from './MapProperties';
import { MultiSelectCalculations } from './MultiSelectCalculations';
import { MultiSelectProperties } from './MultiSelectProperties';
import { PointCloudProperties } from './PointCloudProperties';
import { CadPointProperties, PointProperties } from './PointProperties';
import { useDeviceSize } from '../../../hooks/useDeviceSize';
import { BoxProperties } from './BoxProperties';
import { PointClusterProperties } from './PointClusterProperties';
import { OrthophotoProperties } from './OrthophotoProperties';
import { useOrthophotoLayers } from '../../../hooks/potree/useOrthophotoLayers';
import { RoomProperties } from './RoomProperties';
import { WallSketchProperties } from './WallSketchProperties';
import { OpeningSketchProperties } from './OpeningSketchProperties';
import { SketchObjectProperties } from './SketchObjectProperties';
import { WallSketchElementProperties } from './WallSketchElementProperties';
import { OutlineProperties } from './OutlineProperties';
import { StructureProperties } from './StructureProperties';
import { CadObject, ICadLine, ICadPoint, ICadPolygon } from '../../../utils/CADFileParser';

export enum Tabs {
  ANNOTATIONS = 'ANNOTATIONS',
  CALCULATIONS = 'CALCULATIONS',
  LAYERS = 'LAYERS',
}

/** Tab label with icon, truncated text, and native title tooltip on overflow */
const TabLabel: React.FC<{ icon: React.FC<React.SVGProps<SVGSVGElement>>; label: string; smDevice: boolean }> = ({ icon: Icon, label, smDevice }) => (
  <div className="inline-flex items-center gap-2 select-none min-w-0">
    <Icon className="shrink-0" />
    {!smDevice && (
      <span className="truncate" title={label}>
        {label}
      </span>
    )}
  </div>
);

export const useCalculationTranslations = () => {
  const volume = useT('volume', { swc: true });
  const elevationProfile = useT('elevation profile', { swc: true });
  const invalidCalculations = useT('This object contains invalid calculations');
  const otherCalculations = useT('other calculations in this project');
  return {
    ['VolumeCalculation' as const]: volume,
    ['ProjectionCalculation' as const]: elevationProfile,
    invalidCalculations,
    otherCalculations,
  };
};

interface RendererRightPanelProps {
  project?: ProjectShellFragment;
  closePanel: () => void;
  openPanel: () => void;
  panelOpen: boolean;
  hideArrow?: boolean;
}
const RendererRightPanel_: React.FC2<RendererRightPanelProps> = ({ project, closePanel, openPanel, panelOpen, hideArrow }) => {
  const isReadOnly = useContext(RendererReadOnlyContext);
  const pointClouds = useMemo(() => project?.pointClouds || [], [project?.pointClouds]);
  const calculationTranslations = useCalculationTranslations();

  const { smDevice } = useDeviceSize();

  // Tab label translations
  const propertiesLabel = useT('properties', { swc: true });
  const calculationsLabelText = useT('calculations', { swc: true });
  const commentsLabel = useT('comments', { swc: true });

  const { rightPanelTab } = useQueryParams(['rightPanelTab']);
  // No tab in the URL (a fresh link: the tab is only written once the user switches) = Properties.
  const selectedTab = rightPanelTab || Tabs.ANNOTATIONS;

  const location = useLocation();
  const navigate = useNavigate();

  const onChangeTab = useCallback(
    ({ key }: { key: string }) => {
      if (!panelOpen) openPanel();
      // TabsContainer also calls this on mount with the current tab; writing the URL then
      // (or pushing on every switch) would trap the browser Back button.
      if (key === selectedTab) return;
      navigate(
        {
          ...location,
          search: replaceURLParams({ params: { rightPanelTab: key }, search: location.search }),
        },
        { replace: true },
      );
    },
    [location, navigate, openPanel, panelOpen, selectedTab],
  );
  const { selectedAnnotations, selectedPointCloudName, mapSelected, selectedCadItems, selectedOrthophotoItem, selectedWallSketchElement } = useCustomSelector(
    (state) => state.rendererProvider,
    ['selectedAnnotations', 'selectedPointCloudName', 'mapSelected', 'selectedCadItems', 'selectedOrthophotoItem', 'selectedWallSketchElement'],
  );
  const isSingleSelection = selectedAnnotations.length === 1;
  const annotationsByIdentifier = useAnnotationLookup();
  const annotations = useAnnotationTree();
  const calculationsByIdentifier = useCalculationsByIdentifier();
  const [{ cadObjectsByIdentifier }] = useCADObjects();
  const { orthophotoLayers } = useOrthophotoLayers();
  const orthophotosByIdentifier = keyBy('identifier', orthophotoLayers);

  const selectedAnnotation = isSingleSelection && selectedAnnotations[0]?.type === 'annotation' && annotationsByIdentifier[selectedAnnotations[0].identifier];

  const selectedGroup = isSingleSelection && selectedAnnotations[0]?.type === 'group' && (annotations.find((annotation) => annotation.identifier === selectedAnnotations[0].identifier) as AnnotationContextGroup);

  const selectedPointCloud = useMemo(() => pointClouds.find((pointCloud) => pointCloud.cloudName === selectedPointCloudName), [pointClouds, selectedPointCloudName]);
  const isSingleCadSelection = selectedCadItems?.length === 1;
  const selectedCadObject = isSingleCadSelection && selectedCadItems[0].type === 'cadObject' && cadObjectsByIdentifier[selectedCadItems[0].identifier];

  const selectedOrthophoto = selectedOrthophotoItem && orthophotosByIdentifier[selectedOrthophotoItem.identifier];

  const propertiesPanel = useMemo(() => {
    if (!project) return;
    // While skeleton nodes/edges are selected in the layout editor, the panel
    // is temporarily repurposed to those elements' constraints.
    if (selectedWallSketchElement)
      return <WallSketchElementProperties projectId={project.id} annotationId={selectedWallSketchElement.annotationId} kind={selectedWallSketchElement.kind} elementIds={selectedWallSketchElement.ids} />;
    if (selectedAnnotation && selectedAnnotation.type === 'POINT') return <PointProperties identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'AREA') return <AreaProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'OPENING') return <OpeningProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'DISTANCE') return <DistanceProperties identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'BOX') return <BoxProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'POINTCLUSTER') return <PointClusterProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'ROOM') return <RoomProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'WALL_SKETCH') return <WallSketchProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'OPENING_SKETCH') return <OpeningSketchProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'SKETCH_OBJECT') return <SketchObjectProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'OUTLINE') return <OutlineProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedAnnotation && selectedAnnotation.type === 'STRUCTURE') return <StructureProperties projectId={project.id} identifier={selectedAnnotation.identifier} />;
    if (selectedGroup) {
      if (selectedGroup.type === 'FLOOR') return <FloorProperties projectId={project.id} group={selectedGroup} />;
      // Keyed so the per-surface input drafts start fresh when another surface is selected.
      if (selectedGroup.type === 'SURFACE') return <SurfaceProperties projectId={project.id} group={selectedGroup} key={selectedGroup.identifier} />;
      return <GroupProperties projectId={project.id} group={selectedGroup} />;
    }
    if (selectedAnnotations.length > 1) return <MultiSelectProperties projectId={project.id} selection={selectedAnnotations} />;
    // We set a key on pointcloudproperties to set right height filters when switching between pointclouds (PK-203)
    if (selectedPointCloud) return <PointCloudProperties pointCloud={selectedPointCloud} key={selectedPointCloud.id} />;
    if (mapSelected && project) return <MapProperties project={project} />;
    const isCadPoint = (cadObject: CadObject | false | undefined): cadObject is ICadPoint => {
      return !!cadObject && cadObject.type === 'POINT';
    };
    const isCadLine = (cadObject: CadObject | false | undefined): cadObject is ICadLine => {
      return !!cadObject && cadObject.type === 'LINE';
    };
    const isCadPolygon = (cadObject: CadObject | false | undefined): cadObject is ICadPolygon => {
      return !!cadObject && cadObject.type === 'POLYGON';
    };
    if (isCadPoint(selectedCadObject)) return <CadPointProperties cadPoint={selectedCadObject} />;
    if (isCadLine(selectedCadObject)) return <CadDistanceProperties cadLine={selectedCadObject} />;
    if (isCadPolygon(selectedCadObject)) return <CadAreaProperties cadPolygon={selectedCadObject} />;
    if (selectedOrthophoto) return <OrthophotoProperties orthophoto={selectedOrthophoto} />;
    return null;
  }, [mapSelected, project, selectedAnnotation, selectedGroup, selectedPointCloud, selectedAnnotations, selectedCadObject, selectedOrthophoto, selectedWallSketchElement]);

  const otherCalculationsNav = useMemo(() => {
    if (!(selectedAnnotation && ['AREA', 'DISTANCE'].includes(selectedAnnotation.type))) return null;
    const otherCalculations = omit(selectedAnnotation.identifier, calculationsByIdentifier);

    return <CalculationList title={calculationTranslations.otherCalculations} listedCalculations={Object.values(otherCalculations).flat()} />;
  }, [calculationTranslations.otherCalculations, calculationsByIdentifier, selectedAnnotation]);

  const calculationsPanel = useMemo(() => {
    if (!project) return;
    if (selectedAnnotation && selectedAnnotation.type === 'AREA')
      return (
        <AreaCalculations identifier={selectedAnnotation.identifier} calculations={(calculationsByIdentifier[selectedAnnotation.identifier] || []) as VolumeCalculation[]} otherCalculationsNav={otherCalculationsNav} project={project} />
      );
    if (selectedAnnotation && selectedAnnotation.type === 'DISTANCE')
      return (
        <DistanceCalculations
          identifier={selectedAnnotation.identifier}
          calculations={(calculationsByIdentifier[selectedAnnotation.identifier] || []) as ProjectionCalculation[]}
          otherCalculationsNav={otherCalculationsNav}
          project={project}
        />
      );
    if (selectedGroup) return <GroupCalculations />;
    if (selectedAnnotations.length > 1) return <MultiSelectCalculations />;
    return null;
  }, [selectedAnnotation, calculationsByIdentifier, otherCalculationsNav, selectedGroup, selectedAnnotations, project]);

  const hasInvalidCalculations = useMemo(() => {
    if (!selectedAnnotation) return false;
    return calculationsByIdentifier[selectedAnnotation.identifier]?.some((calculation) => calculation.isOutDated);
  }, [calculationsByIdentifier, selectedAnnotation]);

  const commentsPanel = useMemo(() => {
    if (!project) return null;
    if (selectedAnnotation || selectedGroup) return <Comments project={project} />;
    return null;
  }, [project, selectedAnnotation, selectedGroup]);

  return (
    <div className="relative flex h-full">
      {!hideArrow && (
        <div className="absolute z-30 flex items-center justify-center w-8 h-8 transform -translate-y-1/2 rounded-full cursor-pointer text-neon-green-400 dark:text-ash-green-800 bg-eerie-black dark:bg-neon-green-300 top-1/2 -left-4">
          {panelOpen ? <ChevronRightIcon className="w-4 h-4" onClick={closePanel} /> : <ChevronLeftIcon className="w-4 h-4" onClick={openPanel} />}
        </div>
      )}
      <div
        data-panel="right"
        className={classNames(
          'relative z-10 flex flex-col shrink-0 h-full px-4 py-3 bg-[rgb(230,230,230)] dark:bg-[rgb(33,33,33)] dark:text-white border-t border-gray-200 dark:border-transparent overflow-auto',
          panelOpen ? (smDevice ? 'w-64' : 'w-104') : 'w-20',
        )}
      >
        {panelOpen && (
          <>
            <TabsContainer onChange={onChangeTab} defaultActiveKey={selectedTab}>
              <TabPane key={Tabs.ANNOTATIONS}>
                <TabLabel icon={ChartBoxIcon} label={propertiesLabel} smDevice={smDevice} />
              </TabPane>
              {!selectedPointCloud && (
                <TabPane key={Tabs.CALCULATIONS}>
                  <div className="relative inline-flex items-center gap-2 select-none min-w-0">
                    <CalculatorIcon className="shrink-0" />
                    {!smDevice && (
                      <span className="truncate" title={calculationsLabelText}>
                        {calculationsLabelText}
                      </span>
                    )}
                    {hasInvalidCalculations && <Badge size="s" type="warning" className="absolute cursor-pointer -top-3 -right-3" title={calculationTranslations.invalidCalculations} />}
                  </div>
                </TabPane>
              )}
              {/* Share-link visitors cannot comment, so the tab would only show an empty form. */}
              {!isShareMode() && (
                <TabPane key={Tabs.LAYERS}>
                  <TabLabel icon={CommentsIcon} label={commentsLabel} smDevice={smDevice} />
                </TabPane>
              )}
            </TabsContainer>
            {/* One switch for every control in every panel: a disabled fieldset inertly disables the
                inputs, selects, switches and popover buttons of read-only viewers, whichever panel
                rendered them. Values stay readable and the panel still scrolls. */}
            <fieldset disabled={isReadOnly} className="h-full min-w-0 p-0 pt-4 m-0 overflow-auto border-0 disabled:opacity-80">
              {selectedTab === Tabs.ANNOTATIONS && propertiesPanel}
              {selectedTab === Tabs.CALCULATIONS && calculationsPanel}
              {selectedTab === Tabs.LAYERS && commentsPanel}
            </fieldset>
          </>
        )}
        {!panelOpen && (
          <div className="flex flex-col w-full h-full">
            <div className={classNames('flex items-center justify-center cursor-pointer p-4 rounded-lg', selectedTab === Tabs.ANNOTATIONS && 'bg-neon-green-300 dark:bg-black')} onClick={() => onChangeTab({ key: Tabs.ANNOTATIONS })}>
              <ChartBoxIcon />
            </div>
            <div className={classNames('flex items-center justify-center cursor-pointer p-4 rounded-lg', selectedTab === Tabs.CALCULATIONS && 'bg-neon-green-300 dark:bg-black')} onClick={() => onChangeTab({ key: Tabs.CALCULATIONS })}>
              <CalculatorIcon />
            </div>
            <div className={classNames('flex items-center justify-center cursor-pointer p-4 rounded-lg', selectedTab === Tabs.LAYERS && 'bg-neon-green-300 dark:bg-black')} onClick={() => onChangeTab({ key: Tabs.LAYERS })}>
              <CommentsIcon />
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export const RendererRightPanel = memo(RendererRightPanel_);
